/* eslint-disable no-restricted-globals */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — SONIC controller  (spotify.js)
//
// One controller, two interchangeable backends behind the SAME interface:
//   • MockBackend    — a believable local engine driven by sonic-data.js.
//   • SpotifyBackend — the REAL Spotify Web API (remote-control of the user's
//                      active device). Auth is brokered server-side: the Lambda
//                      owns the client secret + refresh token, and the frontend
//                      pulls short-lived access tokens from /spotify/token via
//                      SpotifyService. No Web Playback SDK, no `streaming` scope.
//
// The UI (sonic.js) only ever talks to the controller, so when the account is
// linked the tray lights up against live data with zero UI changes.
//
// state shape (read-only snapshot handed to subscribers):
//   { status:'playing'|'paused', connection:'mock'|'connecting'|'linked'|'error',
//     account, track:{id,title,artist,album,durMs,art,cover?}, positionMs,
//     shuffle:bool, repeat:'off'|'context'|'track', volume:0..100,
//     deviceId, devices:[{id,name,type,active}], queue:[track], recent:[track],
//     playlists:[{id,name,note,art,trackIds}], note }
// ─────────────────────────────────────────────────────────────────────────

import { TRACKS, PLAYLISTS, DEVICES, QUEUE, RECENT, INITIAL_TRACK, RADIO, trackById } from './sonic-data.js';
import { SpotifyService } from '../services/spotifyService';
import { clamp } from './dom.js';

const SPOTIFY = { api: 'https://api.spotify.com/v1', pollMs: 4000 };

// ── helpers ─────────────────────────────────────────────────────────────────
const REPEAT_CYCLE = { off: 'context', context: 'track', track: 'off' };

// ═════════════════════════════════════════════════════════════════════════
// MOCK BACKEND — believable local engine
// ═════════════════════════════════════════════════════════════════════════
function MockBackend(emit) {
  const s = {
    status: 'paused',
    connection: 'mock',
    account: 'serge · mock session',
    track: INITIAL_TRACK,
    positionMs: 38000,
    shuffle: false,
    repeat: 'off',
    volume: 68,
    deviceId: DEVICES.find(d => d.active).id,
    devices: DEVICES.map(d => ({ ...d })),
    queue: [...QUEUE],
    recent: [...RECENT],
    playlists: PLAYLISTS,
    note: 'LOCAL ENGINE — no account linked',
  };

  let last = performance.now();
  let radioIdx = 0;

  function tick() {
    const now = performance.now();
    const dt = now - last; last = now;
    if (s.status === 'playing') {
      s.positionMs += dt;
      if (s.positionMs >= s.track.durMs) {
        if (s.repeat === 'track') { s.positionMs = 0; }
        else { advance(1, /*auto*/true); return; }
      }
      emit('tick', s);
    }
  }
  const timer = setInterval(tick, 250);

  function pushRecent(t) {
    s.recent = [t, ...s.recent.filter(x => x.id !== t.id)].slice(0, 12);
  }
  function nextFromQueue() {
    if (s.queue.length) return s.queue.shift();
    const t = RADIO[radioIdx % RADIO.length]; radioIdx++;
    return t;
  }
  function advance(dir, auto) {
    if (dir > 0) {
      pushRecent(s.track);
      let nxt = nextFromQueue();
      if (s.shuffle) {
        const pool = TRACKS.filter(t => t.id !== s.track.id);
        nxt = pool[Math.floor(Math.random() * pool.length)];
      }
      s.track = nxt;
    } else {
      if (s.positionMs > 4000) { s.positionMs = 0; emit('state', s); return; }
      const prev = s.recent.shift();
      if (prev) { s.queue.unshift(s.track); s.track = prev; }
    }
    s.positionMs = 0;
    if (auto || s.status === 'playing') s.status = 'playing';
    last = performance.now();
    emit('state', s);
  }

  return {
    snapshot: () => s,
    setActive() {},
    play()   { s.status = 'playing'; last = performance.now(); emit('state', s); },
    pause()  { s.status = 'paused'; emit('state', s); },
    toggle() { s.status = s.status === 'playing' ? 'paused' : 'playing'; last = performance.now(); emit('state', s); },
    next()   { advance(1); },
    prev()   { advance(-1); },
    seek(ms) { s.positionMs = clamp(ms, 0, s.track.durMs); emit('tick', s); },
    setVolume(v) { s.volume = clamp(Math.round(v), 0, 100); emit('state', s); },
    toggleShuffle() { s.shuffle = !s.shuffle; emit('state', s); },
    cycleRepeat()   { s.repeat = REPEAT_CYCLE[s.repeat]; emit('state', s); },
    setDevice(id) {
      s.devices = s.devices.map(d => ({ ...d, active: d.id === id }));
      s.deviceId = id; emit('state', s);
    },
    playTrack(track, context) {
      if (s.track && s.track.id !== track.id) pushRecent(s.track);
      if (Array.isArray(context)) s.queue = context.filter(t => t.id !== track.id);
      s.track = track; s.positionMs = 0; s.status = 'playing';
      last = performance.now(); emit('state', s);
    },
    loadPlaylist(id) {
      const pl = s.playlists.find(p => p.id === id); if (!pl) return;
      const items = pl.trackIds.map(trackById).filter(Boolean);
      if (!items.length) return;
      const [first, ...rest] = s.shuffle ? shuffle(items) : items;
      if (s.track) pushRecent(s.track);
      s.track = first; s.queue = rest; s.positionMs = 0; s.status = 'playing';
      s.note = 'PLAYLIST · ' + pl.name; last = performance.now(); emit('state', s);
    },
    enqueue(track) { s.queue = [...s.queue, track]; emit('state', s); },
    dispose() { clearInterval(timer); },
  };
}

function shuffle(a) { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]]; } return r; }

// ═════════════════════════════════════════════════════════════════════════
// SPOTIFY BACKEND — real Web API. Tokens come from the broker (SpotifyService).
// Same interface as MockBackend; the controller swaps it in once linked.
// ═════════════════════════════════════════════════════════════════════════
function SpotifyBackend(emit) {
  const s = {
    status: 'paused', connection: 'linked', account: 'spotify',
    track: INITIAL_TRACK, positionMs: 0, shuffle: false, repeat: 'off',
    volume: 70, deviceId: null, devices: [], queue: [], recent: [],
    playlists: PLAYLISTS, note: 'LINKED · live playback',
  };
  let active = true;   // gated by tab visibility + tray open (controller toggles)

  async function api(path, opts = {}) {
    const token = await SpotifyService.getToken();
    const res = await fetch(SPOTIFY.api + path, {
      ...opts,
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    if (res.status === 204) return null;
    if (!res.ok) throw new Error('spotify ' + res.status + ' ' + path);
    const ct = res.headers.get('content-type') || '';
    return ct.includes('json') ? res.json() : null;
  }

  function mapTrack(it) {
    if (!it) return s.track;
    return {
      id: it.id, title: it.name,
      artist: (it.artists || []).map(a => a.name).join(', '),
      album: it.album ? it.album.name : '',
      durMs: it.duration_ms || 0,
      art: { h: 196, motif: 'disc' },
      cover: it.album && it.album.images && it.album.images[0] ? it.album.images[0].url : null,
    };
  }
  async function syncPlayer() {
    if (!active || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return;
    try {
      const p = await api('/me/player');
      if (!p) { s.note = 'NO ACTIVE DEVICE — open Spotify on a device'; emit('state', s); return; }
      s.status = p.is_playing ? 'playing' : 'paused';
      s.positionMs = p.progress_ms || 0;
      s.volume = (p.device && typeof p.device.volume_percent === 'number') ? p.device.volume_percent : s.volume;
      s.shuffle = !!p.shuffle_state;
      s.repeat = p.repeat_state || 'off';
      if (p.item) s.track = mapTrack(p.item);
      s.deviceId = p.device ? p.device.id : null;
      s.note = 'LINKED · live playback';
      emit('state', s);
    } catch (e) { s.connection = 'error'; s.note = 'API ERROR — ' + e.message; emit('state', s); }
  }
  async function syncDevices() {
    try { const d = await api('/me/player/devices');
      s.devices = (d.devices || []).map(x => ({ id: x.id, name: x.name.toUpperCase(), type: x.type.toLowerCase().includes('phone') ? 'phone' : x.type.toLowerCase().includes('speaker') ? 'speaker' : 'laptop', active: x.is_active }));
      emit('state', s);
    } catch (e) {}
  }
  // UP NEXT — the player's live queue (currently_playing + queue[]).
  async function syncQueue() {
    if (!active || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return;
    try {
      const q = await api('/me/player/queue');
      if (q && Array.isArray(q.queue)) { s.queue = q.queue.slice(0, 30).map(mapTrack); emit('state', s); }
    } catch (e) {}
  }
  // RECENTLY PLAYED — last tracks (deduped, most-recent first).
  async function syncRecent() {
    try {
      const r = await api('/me/player/recently-played?limit=20');
      if (r && Array.isArray(r.items)) {
        const seen = new Set();
        s.recent = r.items
          .map(i => mapTrack(i.track))
          .filter(t => t && t.id && !seen.has(t.id) && seen.add(t.id))
          .slice(0, 12);
        emit('state', s);
      }
    } catch (e) {}
  }
  // YOUR PLAYLISTS — the user's own + followed playlists.
  async function syncPlaylists() {
    try {
      const r = await api('/me/playlists?limit=50');
      if (r && Array.isArray(r.items)) {
        s.playlists = r.items.filter(Boolean).map(p => ({
          id: p.id,
          name: p.name,
          note: (p.owner && p.owner.display_name ? p.owner.display_name : 'PLAYLIST').toUpperCase(),
          art: { h: 196, motif: 'disc' },
          cover: p.images && p.images[0] ? p.images[0].url : null,
          trackIds: Array(p.tracks ? p.tracks.total : 0),  // sparse — only .length is read
        }));
        emit('state', s);
      }
    } catch (e) {}
  }
  const poll = setInterval(() => { syncPlayer(); syncQueue(); }, SPOTIFY.pollMs);
  syncPlayer(); syncDevices(); syncQueue(); syncRecent(); syncPlaylists();
  // local position interpolation between polls
  let last = performance.now();
  const tickTimer = setInterval(() => {
    const now = performance.now(); const dt = now - last; last = now;
    if (active && s.status === 'playing') { s.positionMs = Math.min(s.positionMs + dt, s.track.durMs); emit('tick', s); }
  }, 250);

  const dev = () => s.deviceId ? '?device_id=' + s.deviceId : '';
  return {
    snapshot: () => s,
    setActive(v) { active = !!v; if (active) { last = performance.now(); syncPlayer(); syncDevices(); syncQueue(); syncRecent(); } },
    play()   { api('/me/player/play' + dev(), { method: 'PUT' }).then(() => { s.status = 'playing'; emit('state', s); }).catch(() => {}); },
    pause()  { api('/me/player/pause' + dev(), { method: 'PUT' }).then(() => { s.status = 'paused'; emit('state', s); }).catch(() => {}); },
    toggle() { s.status === 'playing' ? this.pause() : this.play(); },
    next()   { api('/me/player/next' + dev(), { method: 'POST' }).then(() => setTimeout(() => { syncPlayer(); syncQueue(); syncRecent(); }, 350)).catch(() => {}); },
    prev()   { api('/me/player/previous' + dev(), { method: 'POST' }).then(() => setTimeout(() => { syncPlayer(); syncQueue(); }, 350)).catch(() => {}); },
    seek(ms) { s.positionMs = clamp(ms, 0, s.track.durMs); emit('tick', s); api('/me/player/seek?position_ms=' + Math.round(s.positionMs), { method: 'PUT' }).catch(() => {}); },
    setVolume(v) { s.volume = clamp(Math.round(v), 0, 100); emit('state', s); api('/me/player/volume?volume_percent=' + s.volume, { method: 'PUT' }).catch(() => {}); },
    toggleShuffle() { s.shuffle = !s.shuffle; emit('state', s); api('/me/player/shuffle?state=' + s.shuffle, { method: 'PUT' }).catch(() => {}); },
    cycleRepeat()   { s.repeat = REPEAT_CYCLE[s.repeat]; emit('state', s); api('/me/player/repeat?state=' + s.repeat, { method: 'PUT' }).catch(() => {}); },
    setDevice(id)   { s.deviceId = id; api('/me/player', { method: 'PUT', body: JSON.stringify({ device_ids: [id] }) }).then(() => setTimeout(() => { syncPlayer(); syncDevices(); }, 350)).catch(() => {}); },
    playTrack(track) { api('/me/player/play' + dev(), { method: 'PUT', body: JSON.stringify({ uris: ['spotify:track:' + track.id] }) }).then(() => setTimeout(() => { syncPlayer(); syncQueue(); }, 350)).catch(() => {}); },
    loadPlaylist(id) { api('/me/player/play' + dev(), { method: 'PUT', body: JSON.stringify({ context_uri: 'spotify:playlist:' + id }) }).then(() => setTimeout(() => { syncPlayer(); syncQueue(); }, 350)).catch(() => {}); },
    enqueue(track)  { api('/me/player/queue?uri=spotify:track:' + track.id, { method: 'POST' }).then(() => setTimeout(syncQueue, 350)).catch(() => {}); },
    dispose() { clearInterval(poll); clearInterval(tickTimer); },
  };
}

// ═════════════════════════════════════════════════════════════════════════
// CONTROLLER — the only thing the UI imports
// ═════════════════════════════════════════════════════════════════════════
export function createSonicController() {
  const subs = new Set();
  const emit = (evt, state) => subs.forEach(fn => { try { fn(state, evt); } catch (e) {} });

  let backend = MockBackend(emit);

  function goLive() {
    if (backend.snapshot().connection === 'linked') return;
    backend.dispose();
    backend = SpotifyBackend(emit);
    emit('state', backend.snapshot());
  }

  // On boot, ask the broker whether we're already linked; if so, go live.
  (async () => {
    try {
      const st = await SpotifyService.getStatus();
      if (st && st.linked) goLive();
    } catch (e) { /* stay on the mock engine */ }
  })();

  const api = {
    subscribe(fn) { subs.add(fn); fn(backend.snapshot(), 'state'); return () => subs.delete(fn); },
    get state() { return backend.snapshot(); },
    isLive() { return backend.snapshot().connection === 'linked'; },
    setActive(v) { backend.setActive(v); },
    play()  { backend.play(); },
    pause() { backend.pause(); },
    toggle() { backend.toggle(); },
    next()  { backend.next(); },
    prev()  { backend.prev(); },
    seek(ms) { backend.seek(ms); },
    setVolume(v) { backend.setVolume(v); },
    toggleShuffle() { backend.toggleShuffle(); },
    cycleRepeat() { backend.cycleRepeat(); },
    setDevice(id) { backend.setDevice(id); },
    playTrack(t, ctx) { backend.playTrack(t, ctx); },
    loadPlaylist(id) { backend.loadPlaylist(id); },
    enqueue(t) { backend.enqueue(t); },
    // connection — kick off the brokered OAuth (redirects to Spotify)
    connect() {
      if (api.isLive()) return;
      const snap = backend.snapshot();
      snap.connection = 'connecting'; snap.note = 'REDIRECTING TO SPOTIFY…'; emit('state', snap);
      // Synchronous navigation — must run inside the click's user-activation so
      // Safari doesn't drop the cross-origin redirect (no await before this).
      try { SpotifyService.beginLogin(); }
      catch (e) {
        snap.connection = 'error'; snap.note = 'LINK FAILED — ' + (e && e.message ? e.message : e); emit('state', snap);
        setTimeout(() => { snap.connection = 'mock'; snap.note = 'LOCAL ENGINE — no account linked'; emit('state', snap); }, 4200);
      }
    },
    async refreshStatus() {
      try { const st = await SpotifyService.getStatus(); if (st && st.linked) goLive(); return st; }
      catch (e) { return null; }
    },
    async disconnect() {
      try { await SpotifyService.unlink(); } catch (e) {}
      if (backend.snapshot().connection === 'linked') {
        backend.dispose(); backend = MockBackend(emit); emit('state', backend.snapshot());
      }
    },
    dispose() { try { backend.dispose(); } catch (e) {} subs.clear(); },
  };
  return api;
}
